import {
  ComponentFactoryResolver,
  EventEmitter,
  Injectable,
  ViewContainerRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { Tab } from '../model/tab';
import GenericModel from '../model/generic-model';
import { NavigationEnd, Router } from '@angular/router';
import { SubscriptionBasedComponent } from '../subscription-helpers/subscription-based.component';
import { Project } from '../model/project/project';
import { LoadingService } from './loading.service';
import { IdAssignmentObserver } from '../helpers/id-assignment-observer';
import { BaseProject } from '../model/project/base-project';
import { ModalService } from './modal.service';
import { ProjectTabsContainerComponent } from '../pages/projects/project-tabs-container/project-tabs-container.component';
import { SubProject } from '../model/project/sub-project';
import { CreateViewProjectComponent } from '../pages/projects/client-corner/create-view-project/create-view-project.component';
import { RouteEventsService } from './route-events-service';
import { StringHelper } from '../helpers/string-helper';
import { CollectionHelper } from '../helpers/collection-helper';
import ProductHelper from '../helpers/product-helper';
import { ClientProjectDetailComponent } from '../pages/client-corner/client-project-detail/client-project-detail.component';
import { GeneralHelper } from '../helpers/general-helper';
import { EmployeeTourEditInputStoreService } from './employee-tour-edit/employee-tour-edit-input-store.service';
import { AtelierRecupEventService } from './atelier/atelier-recup-event-service';

@Injectable({
  providedIn: 'root',
})
export class TabsService
  extends SubscriptionBasedComponent
  implements IdAssignmentObserver
{
  private tabs: Tab[] = [];
  private activeTab: Tab;
  private tabRoutes: string[] = [
    'project/',
    'client-project/',
    'job/',
    'client-job/',
    'klant-opdracht/', // TODO phase out client-project and client-job when client project form reimplementations are complete
    'klant-opdracht-aanpassing/',
    'klant-opdracht-weghalen/',
    'klant-opdracht-verkocht-verhuurd',
    'atelier-recup-detail/',
    'admin-project/',
  ];
  private invalidPreviousRoutes: string[] = [
    'client-project/nieuw',
    'client-job/nieuw',
    'klant-opdracht-aanpassing/',
    'klant-opdracht-weghalen/',
    'klant-opdracht-verkocht-verhuurd',
    'admin-project/nieuw',
  ];

  public tabClosed: Subject<any> = new Subject();

  tabContent: ViewContainerRef;

  private previousRoute = '';

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private employeeTourEditInputStoreService: EmployeeTourEditInputStoreService,
    atelierRecupEventService: AtelierRecupEventService,
    private loadingService: LoadingService,
    private router: Router,
    private modalService: ModalService,
    routeEventsService: RouteEventsService
  ) {
    super();
    this.addSubscription(routeEventsService.previousRoutePath, (url) => {
      if (!this.isInvalidPreviousRoute(url)) {
        this.previousRoute = url;
      }
    });
    this.addSubscription(
      atelierRecupEventService.getRecupDetailCloseSubject(),
      () => this.clearTabs()
    );
    this.addSubscription(router.events, (event) => {
      if (event instanceof NavigationEnd) {
        let url = event.url;
        if (url.indexOf(this.tabRoutes[1]) !== -1) {
          this.openProjectTab(url, true);
        } else if (url.indexOf(this.tabRoutes[9]) !== -1) {
          this.openProjectTab(url, false, true, true);
        } else if (url.indexOf(this.tabRoutes[0]) !== -1) {
          this.openProjectTab(url, false);
        } else if (url.indexOf(this.tabRoutes[3]) !== -1) {
          this.openProjectTab(url, true, true);
        } else if (url.indexOf(this.tabRoutes[2]) !== -1) {
          this.openProjectTab(url, false, true);
        } else if (url.indexOf(this.tabRoutes[4]) != -1) {
          this.openClientProjectTab(url);
        } else if (url.indexOf(this.tabRoutes[5]) != -1) {
          this.openClientProjectExecutedTab(url, 'adjustment', 'Aanpassing');
        } else if (url.indexOf(this.tabRoutes[6]) != -1) {
          this.openClientProjectExecutedTab(url, 'repo', 'Weghalen');
        } else if (url.indexOf(this.tabRoutes[7]) != -1) {
          this.openClientProjectExecutedTab(url, 'soldrented', 'Sticker VV');
        } else if (url.indexOf(this.tabRoutes[8]) != -1) {
          this.openClientProjectTab(url, true);
        } else if (!this.routeNeedsTab(url)) {
          let target = this.getTabContentRef();
          if (target) {
            target.detach();
          }
          this.setActiveTab(null);
        }
      }
    });
  }

  private isInvalidPreviousRoute(url: string): boolean {
    return (
      url == null ||
      this.invalidPreviousRoutes.some((r) => url.indexOf(r) !== -1)
    );
  }

  routeNeedsTab(url: string) {
    let needsTabs = false;
    this.tabRoutes.forEach((tabRoute) => {
      if (url.indexOf(tabRoute) > -1) {
        needsTabs = true;
      }
    });
    return needsTabs;
  }

  openProjectTab(
    url,
    client: boolean,
    ipBoardProject?: boolean,
    isAdminCreate?: boolean
  ) {
    let parameters = url.split('/');
    let title = '';
    let componentModel = new BaseProject();
    let projectModel = new Project();
    let inputs = {
      idAssignmentObserver: this,
      client: client,
    };
    if (isAdminCreate) {
      inputs['adminCreate'] = true;
    }
    if (parameters[2] === 'nieuw') {
      title = this.calculateNewProjectTabTitle(
        client,
        ipBoardProject,
        parameters[3]
      );
      if (Number(parameters[3]) > 0) {
        projectModel = new Project(Number(parameters[3]));
        inputs['newSubProjectType'] = 'BaseProject';
      }
    } else {
      let projectId = parameters[2];
      let subId = parameters[3].split('#')[0];
      let fromImmo = parameters[4];
      let queryParams = subId.split('?');
      if (queryParams.length > 1) {
        subId = queryParams[0];
      }
      if (subId === 'aanpassing') {
        subId = null;
        inputs['newSubProjectType'] = 'Adjustment';
      }
      if (subId === 'verkocht-verhuurd') {
        subId = null;
        inputs['newSubProjectType'] = 'SoldRented';
      }
      if (subId === 'weghalen') {
        subId = null;
        inputs['newSubProjectType'] = 'Repo';
      }
      if (!client) {
        inputs['fromImmo'] = fromImmo == null ? false : fromImmo === 'true';
      }
      title = `${ipBoardProject ? '' : 'Project'} #${projectId}`;
      componentModel = new SubProject(subId == null ? null : Number(subId));
      projectModel = new Project(Number(projectId));
    }
    this.openInTab(
      title,
      url,
      isAdminCreate || client
        ? CreateViewProjectComponent
        : ProjectTabsContainerComponent,
      componentModel,
      projectModel,
      inputs
    );
  }

  openClientProjectTab(url: string, forAtelier: boolean = false): void {
    const parameters = url.split('/');
    const projectId = parameters[2];
    const subId = parameters[3];
    const priorityType = parameters[4];
    const inputs = {
      subProjectId: Number(subId),
      priorityType: priorityType,
      forAtelier,
    };
    this.openInTab(
      `#${projectId}`,
      url,
      ClientProjectDetailComponent,
      null,
      new Project(Number(projectId)),
      inputs
    );
  }

  openClientProjectExecutedTab(url: string, type: string, title: string) {
    const parameters = url.split('/');
    const projectId = parameters[2];
    const inputs = {
      type: type,
      title: title,
      form: true,
    };
    this.openInTab(
      `#${projectId} - ${title}`,
      url,
      ClientProjectDetailComponent,
      null,
      new Project(Number(projectId)),
      inputs
    );
  }

  private calculateNewProjectTabTitle(
    client?: boolean,
    ipBoardProject?: boolean,
    projectId?: string
  ): string {
    return client && (projectId == null || projectId === '0')
      ? 'Nieuwe bestelling'
      : client && projectId != null //Drafts
      ? 'Bestelling ' + projectId
      : ipBoardProject
      ? 'Nieuwe job'
      : 'Nieuw project';
  }

  initializeTabsContent(tabContent: ViewContainerRef) {
    if (this.tabContent == null) {
      this.tabContent = tabContent;
      if (this.getActiveTab()) {
        this.insertComponent(this.getActiveTab());
      }
    }
  }

  clearTabContent() {
    this.clearTabs();
    this.tabContent = null;
  }

  getTabContentRef() {
    return this.tabContent;
  }

  openInTab(
    title: string,
    url: string,
    componentClass: any,
    componentModel?: GenericModel,
    projectModel?: Project,
    inputs?: any
  ) {
    this.router.navigateByUrl(url).then(() => {
      const lookup = this.findExistingTab(componentClass, projectModel);
      // Do a lookup of the tab we are trying to register, and if we already have it, reuse it
      let outputs = null;
      if (componentClass === CreateViewProjectComponent) {
        outputs = this.addCloseCurrentTabSubscriptionToOutputs(inputs);
      }
      let activatedTab: Tab;
      if (lookup && this.openingExistingSubproject(componentModel, inputs)) {
        lookup.model = componentModel;
        if (inputs) {
          Object.keys(inputs).map((key) => {
            lookup.component.instance[key] = inputs[key];
          });
        }
        activatedTab = lookup;
      } else if (this.openingOngoingOrder(lookup, url, componentClass)) {
        activatedTab = lookup;
      } else {
        if (lookup != null) {
          this.doOnlyClose(lookup);
        }
        activatedTab = new Tab(
          title,
          url,
          componentClass,
          componentModel,
          projectModel,
          inputs,
          outputs
        );
        this.tabs.push(activatedTab);
      }
      this.insertComponent(activatedTab);
    });
  }

  private openingExistingSubproject(
    componentModel: GenericModel,
    inputs: any
  ): boolean {
    return (
      (componentModel != null && componentModel.id != null) ||
      (inputs != null &&
        inputs.subProjectId != null &&
        GeneralHelper.isNumeric(inputs.subProjectId))
    );
  }

  private openingOngoingOrder(
    lookup: Tab,
    url: string,
    componentClass: any
  ): boolean {
    return (
      lookup != null &&
      lookup.componentClass === lookup.componentClass &&
      lookup.url === url
    );
  }

  addCloseCurrentTabSubscriptionToOutputs(inputs?: any): any {
    let closeCurrentTab = new EventEmitter();
    this.addSubscription(closeCurrentTab, (tabClose) => {
      let componentClass = tabClose.componentClass;
      let project = tabClose.project;
      if (componentClass === 'CreateViewProjectComponent') {
        componentClass = CreateViewProjectComponent;
      }
      let existingTab = this.findExistingTab(componentClass, project);
      if (existingTab != null) {
        let routeTo = '/client-corner/lopende';
        if (inputs != null && !inputs.client) {
          routeTo = '/immo';
        }
        this.closeTab(existingTab, routeTo);
      }
    });
    return { closeCurrentTab: closeCurrentTab };
  }

  insertComponent(tab: Tab): boolean {
    if (this.tabContent) {
      if (!tab.component || tab.component.hostView.destroyed) {
        tab.createComponentView(this.componentFactoryResolver, this.tabContent);
      }
      this.setActiveTab(tab);
      this.tabContent.detach();
      this.tabContent.clear();
      this.tabContent.insert(tab.component.hostView);
      // After inserting, make sure we update the component one more time
      tab.component.changeDetectorRef.markForCheck();
      return true;
    } else {
      console.error('Failed inserting component, retrying...', tab);
      setTimeout(() => this.insertComponent(tab), 200);
      return false;
    }
  }

  private findExistingTab(componentClass: any, project: Project) {
    return this.tabs.find(
      (tab) =>
        tab.component.componentType === componentClass &&
        (project ? tab.project && tab.project.id === project.id : !tab.model)
    );
  }

  getActiveTab(): Tab {
    return this.activeTab;
  }

  setActiveTab(tab: Tab) {
    this.activeTab = tab;
  }

  getTabs() {
    return this.tabs;
  }

  openTab(tab: Tab) {
    this.router.navigateByUrl(tab.url).then(() => {
      this.setActiveTab(tab);
      this.insertComponent(tab);
    });
  }

  closeTab(
    tab: Tab,
    routeTo?: string,
    showConfirmDialog = true,
    persistRouteTo = false
  ) {
    if (
      tab.component != null &&
      tab.component.instance instanceof ProjectTabsContainerComponent
    ) {
      let projectTabsContainerComponent = <ProjectTabsContainerComponent>(
        tab.component.instance
      );
      const doRouteTo = this.getAdminReturnRouteForProject(
        projectTabsContainerComponent.project,
        routeTo
      );
      if (projectTabsContainerComponent.isSaving) {
        this.modalService.confirmDialog(
          'Opgepast',
          'Project is nog niet opgeslagen. Wilt u de tab toch direct sluiten? Pas op: Er kan data verloren gaan. ',
          (confirm) => {
            if (!confirm) {
              return;
            }
            if (projectTabsContainerComponent.fromImmo) {
              this.routeToImmoRedirectLandingPage(
                tab,
                projectTabsContainerComponent,
                doRouteTo
              );
            } else {
              this.performClose(tab, doRouteTo);
            }
          }
        );
      } else if (projectTabsContainerComponent.fromImmo) {
        this.routeToImmoRedirectLandingPage(
          tab,
          projectTabsContainerComponent,
          doRouteTo
        );
      } else {
        this.performClose(tab, doRouteTo);
      }
    } else if (
      tab.component != null &&
      tab.component.instance instanceof CreateViewProjectComponent
    ) {
      let subProject = (<CreateViewProjectComponent>tab.component.instance)
        .model;
      if (routeTo == null && subProject.id == null && showConfirmDialog) {
        this.modalService.confirmDialog(
          'Opgepast',
          'Project is niet opgeslagen. Wilt u de tab toch sluiten? Pas op: De tot nu toe ingevulde informatie zal verloren gaan. ',
          (confirm) => {
            if (!confirm) {
              return;
            }
            const client = tab.inputs != null && !!tab.inputs.client;
            this.closeAfterSubmit(
              tab,
              this.calculateRouteTo(tab.component.instance, routeTo, client)
            );
          }
        );
      } else if (routeTo != null && subProject.id == null) {
        this.closeAfterSubmit(tab, routeTo);
      } else {
        this.performClose(
          tab,
          this.calculateRouteTo(
            tab.component.instance,
            !StringHelper.isEmpty(this.previousRoute)
              ? this.tabs.length > 1
                ? this.previousRoute
                : ''
              : routeTo
          )
        );
      }
    } else if (
      tab.component != null &&
      tab.component.instance instanceof ClientProjectDetailComponent
    ) {
      this.performClose(
        tab,
        this.calculateRouteTo(
          tab.component.instance,
          !persistRouteTo && !StringHelper.isEmpty(this.previousRoute)
            ? this.tabs.length > 1 ||
              !this.previousRoute.includes('klant-opdracht')
              ? this.previousRoute
              : ''
            : routeTo
        )
      );
    }
  }

  calculateRouteTo(instance: any, routeTo: string, client: boolean = true) {
    if (routeTo != null) {
      return routeTo;
    }
    if (instance instanceof ProjectTabsContainerComponent) {
      return '/';
    }
    if (instance instanceof CreateViewProjectComponent) {
      return client ? '/client-corner/home' : '/immo';
    }
  }

  private routeToImmoRedirectLandingPage(
    tab: Tab,
    projectTabsContainerInstance: ProjectTabsContainerComponent,
    routeIfFailure: string
  ): void {
    if (
      projectTabsContainerInstance.model != null &&
      projectTabsContainerInstance.model.id != null &&
      this.employeeTourEditInputStoreService.hasEmployeeTourEditInputForSubProject(
        projectTabsContainerInstance.model.id
      )
    ) {
      this.doOnlyClose(tab);
      this.router.navigate([
        'immo-redirect',
        projectTabsContainerInstance.model.id,
      ]);
    } else {
      this.performClose(tab, routeIfFailure);
    }
  }

  performClose(tab: Tab, routeTo?: string) {
    this.loadingService.clear();
    const index = this.tabs.indexOf(tab);
    tab.closeTab();
    if (index !== -1) {
      this.tabs.splice(index, 1);
      this.tabClosed.next({ closedTab: tab });
      if (this.tabs.length === 0) {
        this.router.navigateByUrl(routeTo == null ? '/projecten' : routeTo);
      }
    }
  }

  private doOnlyClose(tab: Tab): void {
    const index = this.tabs.indexOf(tab);
    tab.closeTab();
    if (index !== -1) {
      this.tabs.splice(index, 1);
    }
  }

  closeAfterSubmit(tab: Tab, routeTo: string): void {
    this.doOnlyClose(tab);
    this.previousRoute = routeTo;
    this.router.navigateByUrl(routeTo);
  }

  clearTabs() {
    this.loadingService.clear();
    this.tabs.forEach((t) => t.closeTab());
    this.tabs.length = 0;
  }

  notifyIdAssignment(
    componentClass: any,
    componentModel: any,
    titlePrefix: string,
    id: number,
    subId: number
  ): void {
    let tab = this.findExistingTab(componentClass, componentModel);
    if (tab != null) {
      tab.title = titlePrefix + id.toString();
      if (subId != null) {
        tab.url = '/project/' + id + '/' + subId;
      }
    }
  }

  private getAdminReturnRouteForProject(
    project: Project,
    alternative?: string
  ): string {
    return project == null ||
      !CollectionHelper.isPopulated(project.products) ||
      !project.products.some((p) => ProductHelper.isIPBoard(p))
      ? alternative
      : '/immo';
  }
}
